import React, {
	useEffect,
	useState,
	useRef,
	useCallback,
	useMemo,
} from "react";

import { Link, useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setLoading, setModule } from "../../store/actions/commonAction";
import CommonDataService from "../../services/common";
import { apiUrl, parseNumber } from "../../helpers/common";

import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Divider from "@mui/material/Divider";
import SaveIcon from "@mui/icons-material/Save";

import dayjs from "dayjs";
import JoditEditor from "jodit-react";
import MessageDialog from "../MessageDialog";
import http from "../../http";

export default function AddCareer({ placeholder }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	let { id } = useParams();
	const editor = useRef(null);

	const token = useSelector((state) => state.commonReducer.user.token);
	const loading = useSelector((state) => state.commonReducer.loading);

	const [message, setMessage] = useState("");
	const [error, setError] = useState(false);

	const [categories, setCategories] = useState([]);
	const [levels, setLevels] = useState([]);
	const [locations, setLocations] = useState([]);
	const [idCategory, setIdCategory] = useState(0);
	const [idLevel, setIdLevel] = useState(0);
	const [idLocation, setIdLocation] = useState(0);
	const [title, setTitle] = useState("");
	const [keyword, setKeyword] = useState("");
	const [content, setContent] = useState("");
	const [publishTime, setPublishTime] = useState(dayjs());
	const [active, setActive] = useState(true);
	const [uniqueName, setUniqueName] = useState("");

	const LoadSelect = useCallback(() => {
		dispatch(setLoading(true));
		dispatch(setModule("Career"));

		CommonDataService.getCareerCategory().then((res) => {
			setCategories(res.data);
		});
		CommonDataService.getCareerLevel().then((res) => {
			setLevels(res.data);
		});
		CommonDataService.getCareerLocation().then((res) => {
			setLocations(res.data);
		});

		if (typeof id !== "undefined") {
			CommonDataService.getCareerById(id).then((res) => {
				setIdCategory(res.data.Row.id_category);
				setIdLevel(res.data.Row.id_level);
				setIdLocation(res.data.Row.id_location);
				setTitle(res.data.Row.title);
				setKeyword(res.data.Row.keyword);
				setContent(res.data.Row.content);
				setPublishTime(dayjs(res.data.Row.publish_time));
				setActive(res.data.Row.active);
				setUniqueName(res.data.Row.unique_name);

				dispatch(setLoading(false));
			});
		} else {
			dispatch(setLoading(false));
		}
	}, [dispatch, id]);

	useEffect(() => {
		LoadSelect();
	}, [LoadSelect]);

	const handleSubmit = (event) => {
		event.preventDefault();

		if (typeof id === "undefined") {
			id = 0;
		}

		dispatch(setLoading(true));

		const data = {
			id: parseNumber(id),
			id_category: parseNumber(idCategory),
			id_level: parseNumber(idLevel),
			id_location: parseNumber(idLocation),
			title: title,
			keyword: keyword,
			content: content,
			publish_time: dayjs(publishTime).format("YYYY-MM-DD HH:mm:ss"),
			active: active,
			unique_name: uniqueName,
		};

		http
			.post("/career/save", data)
			.then((res) => {
				dispatch(setLoading(false));
				navigate("/career" + window.location.search);
			})
			.catch(function (error) {
				setMessage(error.response.data.message);
				setError(true);
				dispatch(setLoading(false));
			});
	};

	const config = useMemo(() => {
		return {
			readonly: false,
			uploader: {
				url: apiUrl() + "/news/upload",
				headers: {
					Authorization: "Bearer " + token,
				},
			},
			imageDefaultWidth: "100%",
			placeholder: placeholder || "Start typings...",
		};
	}, [placeholder, token]);

	return (
		<Grid container spacing={3}>
			<Grid item xs={12}>
				<MessageDialog show={error} message={message} dialogClose={setError} />

				<Paper
					sx={{
						p: 2,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box component="form" onSubmit={(e) => handleSubmit(e)}>
						<Typography variant="h5">Career Form</Typography>

						<Grid container spacing={3}>
							<Grid item xs={12} sm={6}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="id-category-label">Category</InputLabel>
									<Select
										labelId="id-category-label"
										id="id_category"
										name="id_category"
										value={idCategory}
										onChange={(e) => {
											setIdCategory(e.target.value);
										}}
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{categories.map((option, index) => (
											<MenuItem key={index} value={option.id}>
												{option.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="id-level-label">Level</InputLabel>
									<Select
										labelId="id-level-label"
										id="id_level"
										name="id_level"
										value={idLevel}
										onChange={(e) => {
											setIdLevel(e.target.value);
										}}
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{levels.map((option, index) => (
											<MenuItem key={index} value={option.id}>
												{option.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="id-location-label">Location</InputLabel>
									<Select
										labelId="id-location-label"
										id="id_category"
										name="id_category"
										value={idLocation}
										onChange={(e) => {
											setIdLocation(e.target.value);
										}}
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{locations.map((option, index) => (
											<MenuItem key={index} value={option.id}>
												{option.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									required
									label="Title"
									id="title"
									name="title"
									value={title}
									fullWidth
									variant="standard"
									onChange={(e) => setTitle(e.target.value)}
								/>
							</Grid>

							{typeof id !== "undefined" && (
								<Grid item xs={12} sm={6}>
									<TextField
										required
										label="Unique Name"
										id="uniqueName"
										name="uniqueName"
										value={uniqueName}
										fullWidth
										variant="standard"
										onChange={(e) => setUniqueName(e.target.value)}
									/>
								</Grid>
							)}
							<Grid item xs={12} sm={6}>
								<TextField
									label="Keyword"
									id="keyword"
									name="keyword"
									value={keyword}
									fullWidth
									multiline
									rows={4}
									onChange={(e) => setKeyword(e.target.value)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DateTimePicker
										label="Publish Time"
										value={publishTime}
										onChange={(e) => setPublishTime(dayjs(e))}
										renderInput={(params) => <TextField {...params} />}
									/>
								</LocalizationProvider>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControlLabel
									control={
										<Checkbox
											checked={active === "true" || active === true}
											onChange={(e) => setActive(e.target.checked)}
										/>
									}
									label="Active"
								/>
							</Grid>

							<Grid item xs={12} sm={12}>
								<JoditEditor
									ref={editor}
									value={content}
									config={config}
									tabIndex={1} // tabIndex of textarea
									onBlur={(newContent) => setContent(newContent)} // preferred to use only this option to update the content for performance reasons
									onChange={(newContent) => {}}
								/>
							</Grid>

							<Grid item xs={12} sm={12}>
								<Divider />
							</Grid>

							<Grid item xs={12} sm={12}>
								<Button
									variant="contained"
									component={Link}
									to={"/career" + window.location.search}
									startIcon={<ArrowBackIcon />}
									sx={{ mr: 1 }}
								>
									Cancel
								</Button>
								<Button
									type="submit"
									variant="contained"
									disabled={loading}
									startIcon={<SaveIcon />}
								>
									{loading && (
										<span>
											<span className="spinner-border spinner-border-sm"></span>
											&nbsp;
										</span>
									)}
									<span> {loading ? "Please Wait" : "Save"}</span>
								</Button>
							</Grid>
						</Grid>
					</Box>
				</Paper>
			</Grid>
		</Grid>
	);
}
